import { useEffect, useState } from "react";
import { Camera, Plus, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtD } from "@/components/piloting/shared";
import CarouselViewer from "@/components/CarouselViewer";

const CM_PUB_LABELS = { a_valider: "À valider", valide: "Validée", modification: "Modification demandée", planifie: "Planifiée", programme: "Programmée", publie: "Publiée", publication_en_cours: "Publication en cours" };

const isVideo = (url) => /\.(mp4|mov|webm)(\?|$)/i.test(url || "");
const STATUS_LABELS = { a_creer: "À créer", shooting_a_programmer: "Shooting à programmer", shooting_programme: "Shooting programmé", en_production: "En préparation", finalise: "Prêt", pret_a_publier: "Prêt à publier" };
const TYPE_LABELS = { reel: "Reel", photo: "Photo", carrousel: "Carrousel", story: "Story", newsletter: "Newsletter", publicite: "Publicité", autre: "Autre" };

/* Page événement côté client : aperçus des visuels/vidéos de l'événement,
   validation ou demande de modification par élément (synchronisé calendrier CM),
   contenus en préparation, proposition de contenu et shooting. */
export function EventPubsPopup({ event, items, canBook = false, onBook, onClose, onChanged }) {
  const [pubs, setPubs] = useState(null);
  const [changeFor, setChangeFor] = useState(null);
  const [changeText, setChangeText] = useState("");
  const [proposal, setProposal] = useState({ title: "", type: "reel" });
  const [busy, setBusy] = useState(false);

  const linked = items.filter((i) => i.publication_id);
  const pending = items.filter((i) => !i.publication_id);
  const load = () => api.get("/graphisme/espace/cm/publications")
    .then((r) => setPubs(r.data || []))
    .catch((e) => { toast.error(apiError(e)); setPubs([]); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const itemOf = (pid) => linked.find((i) => i.publication_id === pid);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setChangeFor(null); setChangeText(""); load(); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const validate = (p) => act(
    () => api.post(`/graphisme/espace/cm/publications/${p.id}/validate`),
    `« ${p.title} » validée — merci !`);
  const submitChange = (p) => {
    if (!changeText.trim()) { toast.error("Décrivez la modification souhaitée"); return; }
    act(() => api.post(`/graphisme/espace/cm/publications/${p.id}/request-changes`, { text: changeText.trim() }),
      "Modification demandée — votre agence est notifiée");
  };
  const submitProposal = async () => {
    if (!proposal.title.trim()) { toast.error("Titre requis"); return; }
    setBusy(true);
    try {
      await api.post("/content-piloting/portal/items", { title: proposal.title, type: proposal.type, highlight_id: event.id });
      toast.success("Contenu proposé à votre agence");
      setProposal({ title: "", type: "reel" }); onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const renderPub = (p) => {
    const it = itemOf(p.id);
    const toValidate = it?.publication_status === "a_valider" || p.status === "a_valider";
    return (
      <div key={p.id} className="border border-neutral-200 rounded-xl overflow-hidden" data-testid={`event-pub-${p.id}`}>
        {p.format === "carrousel" && (p.slides || []).length > 0 ? (
          <div className="bg-neutral-50" data-testid={`event-pub-carousel-${p.id}`}>
            <CarouselViewer slides={p.slides} title={p.title} testid={`event-pub-carousel-viewer-${p.id}`} />
          </div>
        ) : p.visual_url && isVideo(p.visual_url) ? (
          <video src={p.visual_url} controls playsInline className="w-full max-h-96 object-contain bg-black" data-testid={`event-pub-video-${p.id}`} />
        ) : p.visual_url ? (
          <img src={p.visual_url} alt={p.title} className="w-full max-h-96 object-contain bg-neutral-50" data-testid={`event-pub-img-${p.id}`} />
        ) : null}
        <div className="p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold text-sm truncate">{p.title}</p>
            <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 shrink-0 bg-neutral-50 text-neutral-600 border-neutral-200" data-testid={`event-pub-status-${p.id}`}>
              {it?.publication_status_label || CM_PUB_LABELS[p.status] || p.status}
            </span>
          </div>
          <p className="text-[11px] text-neutral-400">{p.date || "Date à définir"}{p.time ? ` · ${p.time}` : ""}{p.platform ? ` · ${p.platform}` : ""}</p>
          {p.text && <p className="text-xs text-neutral-600 whitespace-pre-line line-clamp-4">{p.text}</p>}
          {toValidate && (
            changeFor === p.id ? (
              <div className="space-y-1.5 pt-1">
                <textarea className="w-full border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs" rows={2}
                  placeholder="Décrivez la modification souhaitée…" value={changeText}
                  onChange={(e) => setChangeText(e.target.value)} data-testid={`event-pub-change-text-${p.id}`} />
                <div className="flex gap-1.5">
                  <Button size="sm" className="h-7 text-xs bg-neutral-900 text-white" disabled={busy} onClick={() => submitChange(p)} data-testid={`event-pub-change-send-${p.id}`}>Envoyer</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setChangeFor(null)} data-testid={`event-pub-change-cancel-${p.id}`}>Annuler</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-1.5 pt-1">
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy}
                  onClick={() => validate(p)} data-testid={`event-pub-validate-${p.id}`}>Valider</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                  onClick={() => { setChangeFor(p.id); setChangeText(""); }} data-testid={`event-pub-changes-${p.id}`}>Demander une modification</Button>
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  const eventPubs = (pubs || []).filter((p) => linked.some((i) => i.publication_id === p.id));
  const othersToValidate = (pubs || []).filter((p) => p.status === "a_valider" && !linked.some((i) => i.publication_id === p.id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[70]" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()} data-testid="event-pubs-popup">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-bold" data-testid="event-pubs-title">{event?.name || "Événement"}</h3>
            <p className="text-[11px] text-neutral-400">
              {event?.event_date ? fmtD(event.event_date) : ""}{event?.offer ? ` · Offre : ${event.offer}` : ""}
            </p>
            <p className="text-[10px] text-neutral-400 mt-0.5">Validez ou demandez une modification pour chaque élément — le calendrier est mis à jour automatiquement.</p>
          </div>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-700" data-testid="event-pubs-close"><X className="w-4 h-4" /></button>
        </div>

        {event?.shooting_date && (
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="event-pubs-shooting">
            <Camera className="w-3.5 h-3.5" /> Shooting programmé le <strong>{fmtD(event.shooting_date)}</strong>
          </p>
        )}
        {!event?.shooting_date && canBook && (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => onBook?.(event)} data-testid="event-pubs-book">
            <Camera className="w-3.5 h-3.5" /> Planifier un shooting
          </Button>
        )}

        {pubs === null && <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>}
        {pubs !== null && eventPubs.length === 0 && pending.length === 0 && othersToValidate.length === 0 && (
          <p className="text-sm text-neutral-400 text-center py-6" data-testid="event-pubs-empty">Aucun contenu pour le moment — votre agence prépare cet événement.</p>
        )}
        {eventPubs.map(renderPub)}
        {othersToValidate.length > 0 && (
          <div className="space-y-3" data-testid="event-pubs-others">
            <p className="text-[11px] font-semibold text-amber-700">Autres publications en attente de validation</p>
            {othersToValidate.map(renderPub)}
          </div>
        )}

        {pending.length > 0 && (
          <div className="border border-neutral-200 rounded-xl p-3 space-y-1" data-testid="event-pubs-pending">
            <p className="text-[11px] font-semibold text-neutral-500">En préparation par votre agence</p>
            {pending.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-2 py-1" data-testid={`event-pending-${i.id}`}>
                <p className="text-xs text-neutral-600 truncate">{i.title} <span className="text-neutral-300">· {TYPE_LABELS[i.type]}</span></p>
                <span className="text-[10px] text-neutral-400 shrink-0">{STATUS_LABELS[i.status]}{i.shooting_date ? ` · tournage ${fmtD(i.shooting_date)}` : ""}</span>
              </div>
            ))}
          </div>
        )}

        {event?.id && (
          <div className="flex gap-1.5">
            <Input className="h-8 text-xs flex-1" placeholder="Proposer un contenu pour cet événement…" value={proposal.title}
              onChange={(e) => setProposal({ ...proposal, title: e.target.value })} data-testid="event-proposal-title" />
            <select className="border border-neutral-200 rounded-lg px-1.5 text-xs bg-white" value={proposal.type}
              onChange={(e) => setProposal({ ...proposal, type: e.target.value })} data-testid="event-proposal-type">
              {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Button size="sm" className="h-8 text-xs bg-[#0B132B] text-white" disabled={busy} onClick={submitProposal} data-testid="event-proposal-add"><Plus className="w-3.5 h-3.5" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
